import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp, User, GitBranch } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useTheme } from '../contexts/ThemeContext';
import { applyAlpha } from '../lib/colorUtils';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import MarketResearchModal from './MarketResearchModal';
import MessageActions from './MessageActions';
import BranchIndicator from './BranchIndicator';

const ChatInterface = ({ 
  conversation, 
  onSendMessage, 
  brandVisualTheme, 
  user, 
  streamingMessage,
  onBranchMessage,
  onOpenBranchTree,
  branchIndex = 1,
  totalBranches = 1,
}) => {
  const [message, setMessage] = useState('');
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [researchContent, setResearchContent] = useState('');
  // Track tested content to reset modal state properly
  const [activeTestContent, setActiveTestContent] = useState(null);
  
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const { resolvedTheme } = useTheme();
  
  // Determine if we're streaming for this conversation
  const isStreaming = streamingMessage && streamingMessage.conversationId === conversation?.id;
  const streamingContent = isStreaming ? streamingMessage.content : '';
  const themeMode = brandVisualTheme?.modes?.[resolvedTheme] ?? brandVisualTheme?.modes?.light;
  const isBrandTheme = resolvedTheme === 'noderiver';
  const heroGradient = themeMode?.heroGradient ?? brandVisualTheme?.gradients?.hero;
  const accentGradient = themeMode?.ctaGradient ?? brandVisualTheme?.gradients?.cta ?? 'linear-gradient(135deg, #1E8997, #0e273a)';
  const backgroundStyle = isBrandTheme
    ? { background: heroGradient }
    : { backgroundColor: themeMode?.background ?? '#ffffff' };
  const textColor = isBrandTheme ? brandVisualTheme?.modes?.noderiver?.text ?? '#ffffff' : themeMode?.text ?? '#0f172a';
  const mutedColor = isBrandTheme ? brandVisualTheme?.modes?.noderiver?.muted ?? 'rgba(255,255,255,0.8)' : themeMode?.muted ?? '#6b7280';
  const accentContrast = themeMode?.accentContrast ?? brandVisualTheme?.palette?.textOnPrimary ?? '#ffffff';
  const surfaceColor = isBrandTheme ? 'rgba(255,255,255,0.08)' : themeMode?.surface ?? '#f3f4f6';
  const borderColor = isBrandTheme ? 'rgba(255,255,255,0.2)' : themeMode?.border ?? '#e5e7eb';
  
  // Dynamic colors for UI elements
  const palette = brandVisualTheme?.palette || {};
  const primaryColor = palette.primary || '#4f46e5'; // Fallback indigo-600
  const primaryLight = applyAlpha(primaryColor, 0.1); // Light background for icons/buttons
  
  const statusColors = {
    working: isBrandTheme ? 'rgba(255,255,255,0.95)' : '#fb923c',
    idle: isBrandTheme ? 'rgba(255,255,255,0.9)' : '#22c55e',
    ready: isBrandTheme ? 'rgba(255,255,255,0.85)' : '#facc15',
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation?.messages, isStreaming, streamingContent]);

  const handleSend = async () => {
    if (!message.trim() || isStreaming) return;
    const content = message;
    setMessage('');
    await onSendMessage(content);
  };

  const submitMessage = async (content) => {
    if (!content.trim() || isStreaming) return;
    await onSendMessage(content);
  };

  const handleOptionSubmit = (selections) => {
    const parts = Object.entries(selections).map(([key, value]) => {
      const label = key.charAt(0).toUpperCase() + key.slice(1);
      return `${label}: ${value}`;
    });
    const content = `Here are the details:\n${parts.join('\n')}`;
    submitMessage(content);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenResearch = (content) => {
    setResearchContent(content);
    setActiveTestContent(content); // Unique ID basically
    setResearchModalOpen(true);
  };

  const parseTimestamp = (value) => {
    if (!value) return null;
    const raw = value instanceof Date ? value : new Date(value);
    if (isNaN(raw.getTime())) return null;
    return raw;
  };

  const formatTime = (timestamp) => {
    const date = parseTimestamp(timestamp);
    if (!date) return "Just now";
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full" style={backgroundStyle}>
      {/* Chat Header */}
      <div
        className="px-6 py-4 border-b"
        style={{
          borderColor,
          backgroundColor: isBrandTheme ? 'rgba(255,255,255,0.05)' : themeMode?.surface ?? '#ffffff',
          color: textColor,
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: accentGradient }}
            >
              <img src="/noderiver.svg" alt="BrandOn Agent" className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: textColor }}>
                BrandOn Agent
              </h2>
              <p className="text-sm" style={{ color: mutedColor }}>
                Your on-brand AI partner
              </p>
            </div>
          </div>
          
          {/* Branch controls */}
          <div className="flex items-center gap-2">
            {/* Branch indicator */}
            <BranchIndicator
              conversation={conversation}
              branchIndex={branchIndex}
              totalBranches={totalBranches}
              onOpenTree={onOpenBranchTree}
              brandVisualTheme={brandVisualTheme}
              isBrandTheme={isBrandTheme}
            />
            
            {/* Branch tree button - always visible when there are messages */}
            {conversation?.messages?.length > 0 && onOpenBranchTree && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onOpenBranchTree}
                      className="h-8 w-8 p-0 rounded-lg transition-colors"
                      style={{
                        color: isBrandTheme ? 'rgba(255,255,255,0.7)' : mutedColor,
                        backgroundColor: 'transparent',
                      }}
                    >
                      <GitBranch className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs px-2 py-1">
                    View branch tree
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea
        className="flex-1 px-6 py-4"
        ref={scrollRef}
        style={{ color: textColor }}
      >
        <div className="space-y-6">
          {conversation?.messages?.map((msg) => (
            <React.Fragment key={msg.id}>
              <div
                className={`flex items-start space-x-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
              >
                {msg.role === 'assistant' && (
                  <Avatar className="w-8 h-8" style={{ background: accentGradient }}>
                    <AvatarFallback className="bg-transparent">
                      <img src="/noderiver.svg" alt="BrandOn" className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                )}
                
                <div
                  className="max-w-[70%] rounded-2xl px-4 py-3"
                  style={
                    msg.role === 'user'
                      ? { background: accentGradient, color: accentContrast }
                      : { backgroundColor: surfaceColor, color: textColor, border: isBrandTheme ? '1px solid rgba(255,255,255,0.1)' : 'none' }
                  }
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  <span className="text-xs opacity-70 mt-2 block" style={{ color: isBrandTheme ? 'rgba(255,255,255,0.7)' : '#6b7280' }}>
                    {formatTime(msg.created_at ?? msg.createdAt)}
                  </span>
                </div>
                
                {msg.role === 'user' && (
                  <Avatar className="w-8 h-8 bg-gray-800">
                    {user?.image ? <AvatarImage src={user.image} /> : null}
                    <AvatarFallback className="bg-transparent">
                      <User className="w-4 h-4 text-white" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              {/* Message Actions */}
              <MessageActions
                message={msg}
                role={msg.role}
                onCopy={() => {}}
                onBranch={onBranchMessage ? (m) => onBranchMessage(m) : undefined}
                onTestAudience={(m) => handleOpenResearch(m.content)}
                showTestAudience={msg.role === 'assistant' && !!msg.preview?.type}
                brandVisualTheme={brandVisualTheme}
                isStreaming={isStreaming}
              />
              {msg.role === 'assistant' && msg.preview?.type === 'clarification' && msg.preview.options && (
                <ClarificationForm 
                  options={msg.preview.options} 
                  onSubmit={handleOptionSubmit} 
                  primaryColor={primaryColor}
                />
              )}
            </React.Fragment>
          ))}
          
          {isStreaming && (
            <div className="flex items-start space-x-3">
              <Avatar className="w-8 h-8" style={{ background: accentGradient }}>
                <AvatarFallback className="bg-transparent">
                  <img src="/noderiver.svg" alt="BrandOn" className="w-4 h-4" />
                </AvatarFallback>
              </Avatar>
              <div
                className="max-w-[70%] rounded-2xl px-4 py-3"
                style={{
                  backgroundColor: surfaceColor,
                  color: textColor,
                  border: isBrandTheme ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                {streamingContent ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {streamingContent}
                    <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse opacity-70" />
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <span 
                      className="text-xs font-medium animate-pulse-subtle animate-gradient-text" 
                      style={{ 
                        backgroundImage: 'linear-gradient(to right, #1E8997, #3b82f6, #8b5cf6, #1E8997)' 
                      }}
                    >
                      BrandOn is thinking...
                    </span>
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: statusColors.working, animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: statusColors.working, animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: statusColors.working, animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div
        className="px-6 py-4 border-t"
        style={{
          borderColor,
          backgroundColor: isBrandTheme ? 'transparent' : themeMode?.surface ?? '#ffffff',
        }}
      >
        {/* Agent Status */}
        <div className="flex items-center space-x-2 mb-3">
          {isStreaming ? (
            <>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: statusColors.working }}></div>
              <span 
                className="text-xs animate-pulse-subtle animate-gradient-text" 
                style={{ 
                  backgroundImage: 'linear-gradient(to right, #1E8997, #3b82f6, #8b5cf6, #1E8997)' 
                }}
              >
                {streamingContent ? 'BrandOn is responding...' : 'BrandOn is thinking...'}
              </span>
            </>
          ) : message.trim() ? (
            <>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors.idle }}></div>
              <span className="text-xs" style={{ color: mutedColor }}>Agent is waiting...</span>
            </>
          ) : (
            <>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors.ready }}></div>
              <span className="text-xs" style={{ color: mutedColor }}>Wakeup Agent</span>
            </>
          )}
        </div>
        
        {/* Input Container */}
        <div className="relative">
          <div
            className="relative rounded-2xl border px-4 py-3 transition-colors"
            style={{
              backgroundColor: isBrandTheme ? 'rgba(255,255,255,0.05)' : themeMode?.input ?? '#f8fafc',
              borderColor,
            }}
          >
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what you need help with..."
              disabled={isStreaming}
              className="w-full min-h-[60px] max-h-[200px] resize-none text-sm bg-transparent pr-14 placeholder:text-gray-500 dark:placeholder:text-gray-400"
              style={{
                border: 'none',
                boxShadow: 'none',
                outline: 'none',
                fontFamily: 'inherit',
                color: textColor,
              }}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || isStreaming}
              className="absolute bottom-3 right-3 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105"
              style={{
                background: message.trim() && !isStreaming ? accentGradient : (isBrandTheme ? 'rgba(255,255,255,0.3)' : '#d1d5db'),
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: message.trim() && !isStreaming ? `0 2px 8px ${applyAlpha(themeMode?.accent ?? '#1E8997', 0.3)}` : 'none',
                color: accentContrast,
              }}
            >
              <ArrowUp className="w-6 h-6" strokeWidth={3} />
            </button>
          </div>
        </div>
        
        <p className="text-xs mt-2" style={{ color: mutedColor }}>
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
      <MarketResearchModal
        open={researchModalOpen}
        onClose={() => setResearchModalOpen(false)}
        conversationId={conversation?.id}
        content={researchContent}
        brandVisualTheme={brandVisualTheme}
        // Force remount when content changes to clear internal state (scores)
        key={activeTestContent} 
      />
    </div>
  );
};

const ClarificationForm = ({ options, onSubmit, primaryColor = '#4f46e5' }) => {
  const [selections, setSelections] = useState({});
  const primaryLight = applyAlpha(primaryColor, 0.1);
  const primaryBorder = applyAlpha(primaryColor, 0.3);

  const handleSelect = (groupId, value) => {
    setSelections(prev => ({
      ...prev,
      [groupId]: value
    }));
  };

  const isComplete = options.every(group => selections[group.id]);

  return (
    <div className="ml-11 mt-3 p-4 border rounded-xl bg-white shadow-sm max-w-md animate-in fade-in slide-in-from-top-1 space-y-4">
      {options.map(group => (
        <div key={group.id} className="space-y-2">
          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{group.label}</h5>
          <div className="flex flex-wrap gap-2">
            {group.options.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleSelect(group.id, opt.value)}
                className="px-3 py-1.5 text-sm rounded-md transition-all border"
                style={
                  selections[group.id] === opt.value
                    ? { backgroundColor: primaryLight, borderColor: primaryBorder, color: primaryColor }
                    : { backgroundColor: '#ffffff', borderColor: '#e5e7eb', color: '#4b5563' }
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      
      <div className="pt-2 border-t border-gray-100 flex justify-end">
        <Button 
          size="sm" 
          className="text-white"
          disabled={!isComplete}
          onClick={() => onSubmit(selections)}
          style={{ backgroundColor: primaryColor }}
        >
          Submit Details
        </Button>
      </div>
    </div>
  );
};

export default ChatInterface;
